import type * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * The containers, after the box-of-boxes grammar was retired.
 *
 * The old `Panel` was a bordered card with a bordered header bar inside it and
 * bordered rows inside that. Three boundaries deep, on a floor its own border
 * colour, which is why every page read as a form from 2005 whatever the palette
 * did.
 *
 * The replacement splits the two jobs a panel was doing badly at once:
 *
 * - `SectionHead` names a region. It sits **on the page ground**, above the
 *   content, with no rule under it. Space below it and space above the next
 *   heading is what separates one region from the next.
 * - `Surface` holds rows. It is the only thing with a background, and it has an
 *   elevation instead of a border. Rows inside it divide with a hairline,
 *   because rows genuinely touch and a hairline is what that means.
 *
 * So a page is now: heading, surface, space, heading, surface. One boundary
 * deep instead of three, and the headings can be read at a glance because they
 * are 17px on the ground rather than 13px inside a bar.
 */

/** A region of a page. Purely semantic; it paints nothing. */
function Section({ className, ...props }: React.ComponentProps<'section'>) {
  return <section data-slot="section" className={cn('min-w-0', className)} {...props} />;
}

/**
 * The name of a region, on the ground above it.
 *
 * `hue` tints the title itself rather than drawing a dot beside it. A 7px
 * square next to a 13px title was a decoration nobody decoded; the word
 * "Failures" in the failure colour is the same information, read without
 * looking for it.
 */
function SectionHead({
  title,
  meta,
  actions,
  hue,
  className,
}: {
  title: React.ReactNode;
  meta?: React.ReactNode;
  actions?: React.ReactNode;
  /** A CSS colour for the title. Reserved for regions whose whole subject is
      one semantic state: the gate indigo, the failure red. A neutral region
      passes nothing. */
  hue?: string;
  className?: string;
}) {
  return (
    <div
      data-slot="section-head"
      className={cn('mb-2.5 flex min-w-0 items-baseline gap-2.5 px-0.5', className)}
    >
      <h2 className="dy-section-title min-w-0 truncate" style={hue ? { color: hue } : undefined}>
        {title}
      </h2>
      {meta ? <span className="text-fg-3 tnum min-w-0 truncate text-xs">{meta}</span> : null}
      {actions ? <div className="ml-auto flex shrink-0 items-center gap-1.5">{actions}</div> : null}
    </div>
  );
}

/**
 * The one thing in the app with a background.
 *
 * `tone` washes the surface in a semantic colour at region scale. It exists for
 * exactly one caller -- the gate list -- because the gates are what the product
 * is about and a page where the important region merely has a different 7px dot
 * has not said so.
 */
function Surface({
  className,
  tone,
  raised,
  ...props
}: React.ComponentProps<'div'> & {
  tone?: 'gate';
  /** The subject of the page rather than one region of several. */
  raised?: boolean;
}) {
  return (
    <div
      data-slot="surface"
      className={cn(
        'overflow-hidden',
        raised ? 'dy-surface-2' : 'dy-surface',
        tone === 'gate' && 'dy-gate-wash',
        className,
      )}
      {...props}
    />
  );
}

/**
 * A group heading inside a surface: sticky, so the group a row belongs to is
 * still named when you have scrolled into the middle of it. Unlike the old
 * one it has no border under it, only its own weight and the space around it.
 */
function GroupHead({
  label,
  count,
  tone,
  actions,
  className,
}: {
  label: string;
  count?: number;
  tone?: string;
  actions?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      data-slot="group-head"
      className={cn(
        'bg-card/92 sticky top-0 z-[1] flex items-center gap-2 px-gutter pt-3 pb-1.5 backdrop-blur-[2px]',
        className,
      )}
    >
      <span className={cn('dy-eyebrow', tone)}>{label}</span>
      {count === undefined ? null : (
        <span className={cn('text-fg-3 tnum text-2xs', !actions && 'ml-auto')}>{count}</span>
      )}
      {actions ? <div className="ml-auto flex items-center gap-1">{actions}</div> : null}
    </div>
  );
}

/** A well inside a surface: a form, a reason field, a run summary. */
function Inset({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div data-slot="inset" className={cn('dy-inset px-gutter py-2.5', className)} {...props} />
  );
}

/**
 * A list of rows inside a surface. The hairline between rows is the only border
 * left in the content area of the app, and it is here because two rows touching
 * is the one relationship a hairline actually describes.
 */
function Rows({ className, ...props }: React.ComponentProps<'ul'>) {
  return <ul data-slot="rows" className={cn('divide-line divide-y', className)} {...props} />;
}

export { GroupHead, Inset, Rows, Section, SectionHead, Surface };
